/**
 * API client — direct calls to the gateway (single origin), bearer injected
 * from the OIDC session. Replaces every Next.js BFF route.
 *
 * On 401: one silent-renew attempt, then retry once; a second 401 surfaces.
 */

import { getConfig } from '@/config';
import { getAccessToken, getUserManager } from '@/auth/oidc';
import { noteNoKarexiaAccess } from '@/auth/karexiaAccess';
import {
  emitCreditsCharged,
  emitInsufficientCredits,
  parseCreditsCharged,
  parseInsufficientCredits,
} from '@/lib/api/ai-credits';
import { emitPlanRequired, parsePlanRequired } from '@/lib/api/plan-gate';
import { tr, useLocaleStore } from '@/i18n';

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message?: string,
  ) {
    super(message ?? tr('shell.lib.apiClient.apiErrorStatus', { status }));
  }
}

/**
 * User-facing French message for a caught error. Network failures (fetch
 * rejects with `TypeError: Failed to fetch` when the server is down / a
 * 5xx preflight has no CORS headers / DNS/offline) are reported as
 * status 0 so callers can render a clean "service unavailable" string
 * instead of the raw browser "Failed to fetch".
 */
export function isNetworkError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 0;
}

/**
 * True for an HTTP 429 (the gateway's per-user rate limit — 120 req/min on QA).
 * Callers that degrade 404/501/503 to an empty state must NOT treat 429 the same
 * way: a rate limit means "retry shortly", not "no data" — swallowing it renders
 * a misleading empty screen (this is exactly how a 429 once surfaced as
 * "Impossible de charger le catalogue de personas").
 */
export function isRateLimited(err: unknown): boolean {
  return err instanceof ApiError && err.status === 429;
}

/**
 * Friendly French message for an API error (settings pages): maps 403/404/409/
 * 422 instead of leaking the raw "API error 409".
 */
export function describeApiError(err: unknown, fallback = tr('shell.lib.apiClient.somethingWentWrongPleaseTryAgain')): string {
  if (err instanceof ApiError) {
    if (err.status === 429 || err.status === 0) return err.message;
    const detail = errorDetailText(err.body);
    const safe = detail && !/^[\[{]/.test(detail) ? detail : null;
    switch (err.status) {
      case 401:
        return tr('shell.lib.apiClient.sessionExpiredSignInAgainThen');
      case 403:
        return tr('shell.lib.apiClient.actionReservedForTheAccountOwners');
      case 404:
        return tr('shell.lib.apiClient.itemNotFoundItMayHave');
      case 409:
        return safe ?? tr('shell.lib.apiClient.conflictThisItemAlreadyExistsOr');
      case 422:
        return tr('shell.lib.apiClient.invalidDataCheckTheFieldsEntered');
      default:
        if (err.status >= 500) return tr('shell.lib.apiClient.theServiceRanIntoAProblem');
    }
    return fallback;
  }
  if (err instanceof Error && err.message && !/^API error/.test(err.message)) return err.message;
  return fallback;
}

const NETWORK_DOWN_MESSAGE =
  tr('shell.lib.apiClient.serviceTemporarilyUnavailableCheckYourCo');

const RATE_LIMITED_MESSAGE =
  tr('shell.lib.apiClient.tooManyRequestsLimitTemporarilyReached');

// ---------------------------------------------------------------------------
// Active workspace (agency brand switcher). When set, every request carries
// `X-Workspace-Id`. Persisted per browser (try/catch: storage may be blocked);
// null/absent = no header = the tenant's default scope (today's behaviour).
// ---------------------------------------------------------------------------
const WORKSPACE_KEY = 'psn-active-workspace';
let activeWorkspaceId: string | null = null;
try {
  activeWorkspaceId = localStorage.getItem(WORKSPACE_KEY) || null;
} catch {
  activeWorkspaceId = null;
}

export function getActiveWorkspaceId(): string | null {
  return activeWorkspaceId;
}

export function setActiveWorkspaceId(id: string | null): void {
  activeWorkspaceId = id || null;
  try {
    if (activeWorkspaceId) localStorage.setItem(WORKSPACE_KEY, activeWorkspaceId);
    else localStorage.removeItem(WORKSPACE_KEY);
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Acting tenant (cross-account delegation). When a managed account is selected
// (the "Comptes gérés" header switcher), every request carries
// `X-Acting-Tenant: <ownerTenantId>` so the gateway resolves the caller's
// DELEGATED scope inside that owner's account (the backend still enforces the
// exact per-resource/per-platform grant — the header only names whose account
// to act in). Persisted in sessionStorage so a reload keeps the active scope,
// and deliberately NOT localStorage so it dies with the tab/session; cleared on
// logout. null/absent = no header = the caller's OWN account (today's
// behaviour). Orthogonal to X-Workspace-Id above (both may be present).
// ---------------------------------------------------------------------------
const ACTING_TENANT_KEY = 'psn-acting-tenant';
let actingTenantId: string | null = null;
try {
  actingTenantId = sessionStorage.getItem(ACTING_TENANT_KEY) || null;
} catch {
  actingTenantId = null;
}

export function getActingTenantId(): string | null {
  return actingTenantId;
}

export function setActingTenantId(id: string | null): void {
  actingTenantId = id || null;
  try {
    if (actingTenantId) sessionStorage.setItem(ACTING_TENANT_KEY, actingTenantId);
    else sessionStorage.removeItem(ACTING_TENANT_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Add the ambient request-context headers (X-Workspace-Id, X-Acting-Tenant) to
 * a Headers/HeadersInit. Shared by `apiFetch` and by raw-`fetch` helpers
 * (SSE streams, blob downloads) that cannot go through `apiFetch`, so
 * delegation / workspace scoping applies uniformly.
 *
 * Also carries `Accept-Language: <current UI locale>` (en | fr | ar) so the
 * server localizes catalog text (plans, add-ons, connector templates, AI
 * registry, copilot tools). A caller-provided Accept-Language wins.
 */
export function withContextHeaders(init?: HeadersInit): Headers {
  const headers = new Headers(init);
  if (!headers.has('Accept-Language')) {
    headers.set('Accept-Language', useLocaleStore.getState().locale);
  }
  if (activeWorkspaceId && !headers.has('X-Workspace-Id')) {
    headers.set('X-Workspace-Id', activeWorkspaceId);
  }
  if (actingTenantId && !headers.has('X-Acting-Tenant')) {
    headers.set('X-Acting-Tenant', actingTenantId);
  }
  return headers;
}

/** Window events fired when the server rejects a persisted (stale) context. */
export const ACTING_TENANT_INVALID_EVENT = 'psn:acting-tenant-invalid';
export const WORKSPACE_INVALID_EVENT = 'psn:workspace-invalid';

function errorDetailText(body: unknown): string {
  if (typeof body === 'string') return body;
  if (body && typeof body === 'object') {
    const d = (body as { detail?: unknown; message?: unknown }).detail ?? (body as { message?: unknown }).message;
    if (typeof d === 'string') return d;
    if (d && typeof d === 'object') {
      try {
        return JSON.stringify(d);
      } catch {
        return '';
      }
    }
  }
  return '';
}

/**
 * A 401/403 whose detail names the delegation / acting context, or a 400/403/404
 * naming the workspace, means the persisted header is dead (grant revoked or
 * expired, workspace deleted). Drop it so the app falls back to the caller's own
 * account / whole tenant instead of 403-ing every page.
 */
function dropStaleContext(status: number, body: unknown): void {
  const text = errorDetailText(body);
  if (!text) return;
  if (actingTenantId && (status === 401 || status === 403) && /deleg|acting|grant|délég/i.test(text)) {
    setActingTenantId(null);
    try {
      window.dispatchEvent(new CustomEvent(ACTING_TENANT_INVALID_EVENT));
    } catch {
      /* ignore */
    }
  }
  if (activeWorkspaceId && (status === 400 || status === 403 || status === 404) && /workspace|espace de travail/i.test(text)) {
    setActiveWorkspaceId(null);
    try {
      window.dispatchEvent(new CustomEvent(WORKSPACE_INVALID_EVENT));
    } catch {
      /* ignore */
    }
  }
}

async function doFetch(path: string, init: RequestInit, token: string | null): Promise<Response> {
  const { apiBaseUrl } = getConfig();
  const headers = withContextHeaders(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !headers.has('Content-Type') && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  try {
    return await fetch(`${apiBaseUrl}${path}`, { ...init, headers });
  } catch {
    // fetch() only rejects on a network-level failure (server unreachable,
    // blocked preflight, offline). Normalise it to a status-0 ApiError with a
    // localized message so no component ever surfaces the raw "Failed to fetch".
    throw new ApiError(0, null, NETWORK_DOWN_MESSAGE);
  }
}

export async function apiFetch<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  let token = await getAccessToken();
  let response = await doFetch(path, init, token);

  if (response.status === 401 && token) {
    try {
      const user = await getUserManager().signinSilent();
      token = user?.access_token ?? null;
      if (token) {
        response = await doFetch(path, init, token);
      }
    } catch {
      // silent renew failed — the 401 below surfaces and the guard re-logs-in
    }
  }

  if (!response.ok) {
    // Read the body ONCE as text, then try JSON — response.json() consumes the
    // stream even on parse failure, which would make a text() fallback throw.
    let body: unknown = null;
    const raw = await response.text().catch(() => '');
    try {
      body = raw ? JSON.parse(raw) : null;
    } catch {
      body = raw || null;
    }
    if (response.status === 429) {
      // Give a rate limit a localized, actionable message (honouring Retry-After
      // when the limiter sends it) instead of the opaque "API error 429".
      const retryAfter = response.headers.get('Retry-After');
      const suffix = retryAfter ? tr('shell.lib.apiClient.tryAgainInRetryafterS', { retryAfter }) : '';
      throw new ApiError(429, body, RATE_LIMITED_MESSAGE + suffix);
    }
    dropStaleContext(response.status, body);
    // Shared-realm account without any Karexia client role: the gateway answers
    // 403 {error: "no_karexia_access"}. Flip the global flag so the ProtectedRoute
    // gate shows the "no Karexia access" screen, then still throw.
    noteNoKarexiaAccess(response.status, body);
    const insufficient = parseInsufficientCredits(response.status, body);
    if (insufficient) {
      // Surface the AI-credit outage GLOBALLY (a refill modal + toast, wherever
      // the AI call was made) via a window event the CreditGuardHost listens on,
      // then still throw so the call site's own handling runs unchanged. Works
      // across every backend 402 shape (see lib/api/ai-credits.ts).
      emitInsufficientCredits(insufficient);
    }
    const planRequired = parsePlanRequired(response.status, body);
    if (planRequired) {
      // S8 — a server-side plan-feature refusal (403 feature_not_in_plan /
      // plan_required): flip every <PlanGate feature=…> to the upgrade card and
      // let the shell's PlanRequiredHost show the upgrade dialog, then still throw.
      emitPlanRequired(planRequired);
    }
    throw new ApiError(response.status, body);
  }

  if (response.status === 204) return undefined as T;
  const data = (await response.json()) as T;
  // An AI action that settled credits carries `credits: {charged, balance_after}`
  // (body, or X-AI-Credits-* headers): tell the shell so it can update the visible
  // balance immediately and toast « X crédits utilisés · solde Y ».
  const charged = parseCreditsCharged(data, response.headers);
  if (charged) emitCreditsCharged(charged);
  return data;
}

export const api = {
  // `init` lets a caller pass request options such as an AbortSignal (e.g. a
  // load timeout so a hung upstream can never leave a page stuck on loading).
  get: <T>(path: string, init?: RequestInit) => apiFetch<T>(path, init),
  // `init` lets a caller opt into request options such as `keepalive` (so a POST
  // fired optimistically completes even if the page navigates/reloads right
  // after). method + body always win over anything spread from init.
  post: <T>(path: string, body?: unknown, init?: RequestInit) =>
    apiFetch<T>(path, {
      ...init,
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  put: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  // `init` lets a caller opt into request options such as `keepalive` (so an
  // optimistic PATCH completes even if the page navigates/reloads right after).
  // method + body always win over anything spread from init.
  patch: <T>(path: string, body?: unknown, init?: RequestInit) =>
    apiFetch<T>(path, {
      ...init,
      method: 'PATCH',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};
